import * as React from 'react';
import { SharedStepState } from './Step';
import { Route, Redirect, Switch } from 'react-router';
import { PageRoutes } from '../root/Routes';
import { RouteComponentProps } from 'react-router-dom';

export interface WizardProperties extends RouteComponentProps {
    computedMatch: { params: any };
    stepNameParameter: string;
    path: string;
}

export interface WizardState<T> {
    stepHistory: T[];
}

// T - parameters
// U - state
// V - step name type
export default abstract class Wizard<
    T extends WizardProperties,
    U extends WizardState<V>,
    V
> extends React.Component <T, U> {

    private readonly STEP_PARAMETER_NAME = 'stepName';

    constructor(props: any) {
        super(props);

        this.goBack = this.goBack.bind(this);
        this.handleBackButton = this.handleBackButton.bind(this);
    }

    render() {
        return (
            <Switch>
                <Route
                    path={`${this.props.path}/:${this.STEP_PARAMETER_NAME}`}
                    render={(props: any) => {
                        let urlStepName = props.match.params[this.STEP_PARAMETER_NAME];
                        return this.getStepElementByUrlStepName(urlStepName);
                    }}
                />
                <Redirect to={`${this.props.path}/${this.stepKeyToUrlStepName(this.getFirstStepKey())}`} />
            </Switch>
        );
    }

    componentWillMount() {
        if (this.state.stepHistory.length === 0) {
            this.goToStep(this.getFirstStepKey());
        }

        window.addEventListener('popstate', this.handleBackButton);
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.handleBackButton);
    }

    protected abstract createStepElement(stepKey: V): JSX.Element | null;
    protected abstract urlStepNameToStepKey(urlStepName: string): V | null;
    protected abstract stepKeyToUrlStepName(stepKey: V): string;
    protected abstract getFirstStepKey(): V;

    protected goToStep(stepKey: V, additionalState: object = {}): void {
        let modifiedStepHistory = this.addStepToCopiedStepHistory(stepKey);
        let newState = {
            stepHistory: modifiedStepHistory
        };
        this.mergeStepState(newState, additionalState);
    }

    protected goBack(additionalState: object = {}) {
        let newState = {
            stepHistory: this.removeStepFromCopiedStepHistory(),
            ...additionalState 
        };
        this.setState(newState, this.navigateToLastStepInHistory);
    }

    protected getCurrentStepKey(): V {
        let currentStepKey = this.state.stepHistory.slice(-1)[0];
        return currentStepKey;
    }

    protected getPreviousStepKey(): V | null {
        if (this.state.stepHistory.length > 1) {
            let previousStepKey = this.state.stepHistory.slice(-2)[0];
            return previousStepKey;
        } else {
            return null;
        }
    }

    protected mergeStepStateAndContinue(stepState: SharedStepState, nextStepKey: V, additionalState: object = {}): void {
        let modifiedStepHistory = this.addStepToCopiedStepHistory(nextStepKey);
        let finalAdditionalState = {
            stepHistory: modifiedStepHistory,
            ...additionalState
        };
        this.mergeStepState(stepState, finalAdditionalState);
    }

    private navigateToLastStepInHistory(): void {
        let urlStepName = this.stepKeyToUrlStepName(this.getCurrentStepKey());
        this.props.history.push(urlStepName);
    }

    private handleBackButton(): void {
        if (this.state.stepHistory.length > 1) {
            this.goBack();
        } else {
            this.props.history.push(PageRoutes.Dashboard);
        }
    }

    private getStepElementByUrlStepName(urlStepName: string) {
        let stepElement: JSX.Element | null = null;

        let stepKey = this.urlStepNameToStepKey(urlStepName);
        if (stepKey) {
            stepElement = this.createStepElement(stepKey);
        } else {
            this.props.history.push(PageRoutes.Dashboard);
        }

        return stepElement;
    }

    private mergeStepState(stepState: SharedStepState, additionalState: object | null = null): void {
        let newState: any = {};
        for (let property in stepState) {
            if (stepState.hasOwnProperty(property)) {
                newState[property] = stepState[property];
            }
        }

        if (additionalState) {
            newState = {
                ...newState,
                ...additionalState
            };
        }
        this.setState(newState, this.navigateToLastStepInHistory);
    }

    private addStepToCopiedStepHistory(newStepKey: V): V[] {
        let currentHistory = [ ...this.state.stepHistory ];
        currentHistory.push(newStepKey);
        return currentHistory;
    }

    private removeStepFromCopiedStepHistory(): V[] {
        let currentHistory = [ ...this.state.stepHistory ];
        currentHistory.pop();
        return currentHistory;
    }
}



// WEBPACK FOOTER //
// ./src/webportal/components/wizard/Wizard.tsx